import React from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Container,
  InputAdornment,
  Grid,
} from '@mui/material';
import {
  EmailOutlined,
  LockOutlined,
  CheckCircleOutline,
  AutoAwesome,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { gtmNewsletterSubmit } from '../../utils/gtm';

/* ── Decorative mini-card for the right column illustration ── */
const GlassCard = ({ icon, label, sx: sxOverride }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      px: 2.5,
      py: 1.5,
      borderRadius: '14px',
      bgcolor: 'rgba(255,255,255,0.08)',
      backdropFilter: 'blur(8px)',
      border: '1px solid rgba(255,255,255,0.12)',
      color: '#FFFFFF',
      fontSize: '14px',
      fontWeight: 600,
      whiteSpace: 'nowrap',
      ...sxOverride,
    }}
  >
    <Box component="span" sx={{ fontSize: '20px', lineHeight: 1 }}>{icon}</Box>
    {label}
  </Box>
);

/* ── Benefit bullet ── */
const Benefit = ({ text }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
    <CheckCircleOutline
      sx={{ fontSize: 18, color: 'rgba(255,255,255,0.7)' }}
    />
    <Typography
      sx={{ fontSize: '15px', color: '#FFFFFF', lineHeight: 1.5 }}
    >
      {text}
    </Typography>
  </Box>
);

const NewsletterSection = () => {
  const { t } = useTranslation();
  const { lng } = useParams();
  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        background:
          'linear-gradient(180deg, #1E3A5F 0%, #0F172A 100%)',
        pt: { xs: 4, md: 5 },
        pb: { xs: 4, md: 6 },
        /* Radial glow — softened */
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse at 70% 50%, rgba(46,79,122,0.15) 0%, transparent 55%)',
          pointerEvents: 'none',
        },
        /* Faint decorative circle — subtler */
        '&::after': {
          content: '""',
          position: 'absolute',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          border: '1px solid rgba(255,255,255,0.03)',
          top: '-200px',
          right: '-100px',
          pointerEvents: 'none',
        },
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: '960px',
          mx: 'auto',
          px: { xs: 2, md: 3 },
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Grid container spacing={4} alignItems="center">
          {/* ── Left column: content ── */}
          <Grid item xs={12} md={7}>
            {/* Overline */}
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 600,
                color: 'rgba(255,255,255,0.7)',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                mb: 1.5,
                textAlign: { xs: 'center', md: 'left' },
              }}
            >
              🍁 {t('newsletter.overline')}
            </Typography>

            {/* Headline */}
            <Typography
              sx={{
                fontSize: { xs: '19px', md: '22px' },
                fontWeight: 700,
                color: '#FFFFFF',
                lineHeight: 1.3,
                mb: 0.5,
                letterSpacing: '-0.01em',
                textAlign: { xs: 'center', md: 'left' },
              }}
            >
              {t('newsletter.headline')}
            </Typography>

            {/* Benefit bullets */}
            <Box
              sx={{
                mb: 3,
                mt: 1.5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: { xs: 'center', md: 'flex-start' },
              }}
            >
              <Benefit text={t('newsletter.benefit1')} />
              <Benefit text={t('newsletter.benefit2')} />
              <Benefit text={t('newsletter.benefit3')} />
            </Box>

            {/* Input + Button */}
            <Box
              component="form"
              action="https://buttondown.com/api/emails/embed-subscribe/Finmaple"
              method="post"
              onSubmit={() => {
                if (window.plausible) window.plausible('newsletter_submit', { props: { source: 'footer' } });
                gtmNewsletterSubmit({ source: 'footer' });
              }}
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: '16px',
                maxWidth: '520px',
                mx: { xs: 'auto', md: 0 },
                mb: 2,
              }}
            >
              <TextField
                id="newsletter-email"
                name="email"
                type="email"
                required
                variant="outlined"
                placeholder={t('newsletter.placeholder')}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlined
                        sx={{
                          fontSize: 18,
                          color: 'rgba(255,255,255,0.5)',
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    height: '50px',
                    borderRadius: '12px',
                    fontSize: '14px',
                    bgcolor: 'rgba(255,255,255,0.08)',
                    color: '#FFFFFF',
                    '& fieldset': {
                      borderColor: 'rgba(255,255,255,0.15)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255,255,255,0.35)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#FFFFFF',
                    },
                  },
                  '& .MuiOutlinedInput-input::placeholder': {
                    color: 'rgba(255,255,255,0.5)',
                    opacity: 1,
                  },
                }}
              />
              <Button
                type="submit"
                variant="contained"
                startIcon={<AutoAwesome />}
                sx={{
                  height: '50px',
                  px: 4,
                  fontSize: '15px',
                  fontWeight: 700,
                  borderRadius: '12px',
                  backgroundColor: '#FFFFFF',
                  color: '#1E3A5F',
                  textTransform: 'none',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.22s ease',
                  '&:hover': {
                    backgroundColor: '#F0F4F8',
                    boxShadow: '0 8px 28px rgba(0,0,0,0.2)',
                    transform: 'translateY(-2px)',
                  },
                  '&:active': {
                    transform: 'scale(0.97)',
                  },
                }}
              >
                {t('newsletter.subscribe')}
              </Button>
            </Box>

            {/* Trust microcopy */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                flexWrap: 'wrap',
                justifyContent: { xs: 'center', md: 'flex-start' },
              }}
            >
              <LockOutlined
                sx={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }}
              />
              <Typography
                sx={{
                  fontSize: '12px',
                  color: 'rgba(255,255,255,0.5)',
                  lineHeight: '16px',
                }}
              >
                {t('newsletter.trust')}{' '}
                <Typography
                  component={RouterLink}
                  to={`/${lng}/como-monetizamos`}
                  sx={{
                    fontSize: '12px',
                    color: 'rgba(255,255,255,0.5)',
                    textDecoration: 'underline',
                    transition: 'color 0.2s ease',
                    '&:hover': { color: '#FFFFFF' },
                  }}
                >
                  {t('newsletter.privacy')}
                </Typography>
              </Typography>
            </Box>
          </Grid>

          {/* ── Right column: decorative illustration ── */}
          <Grid
            item
            md={5}
            sx={{
              display: { xs: 'none', md: 'flex' },
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '260px',
                height: '220px',
              }}
            >
              {/* Card 1 — top, rotated */}
              <Box sx={{ position: 'absolute', top: 0, left: 10, transform: 'rotate(-4deg)' }}>
                <GlassCard icon="📊" label={t('newsletter.card1')} />
              </Box>

              {/* Card 2 — middle */}
              <Box sx={{ position: 'absolute', top: 75, left: 30 }}>
                <GlassCard icon="💳" label={t('newsletter.card2')} />
              </Box>

              {/* Card 3 — bottom, rotated */}
              <Box sx={{ position: 'absolute', top: 150, left: 15, transform: 'rotate(3deg)' }}>
                <GlassCard icon="📅" label={t('newsletter.card3')} />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default NewsletterSection;
